import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
  Switch,
  Row,
  Col,
  Select,
  Divider,
} from "antd";
import {
  SettingOutlined,
  SecurityScanOutlined,
  BellOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { Title, Text } = Typography;
const { Option } = Select;

const SuperAdminAccountSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      await apiClient.put("/superadmin/account-settings", values);
      message.success("Account settings saved successfully");
    } catch (error) {
      message.error("Failed to save account settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={2}>Account Settings</Title>

      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <>
                <SettingOutlined /> Preferences
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                language: "en",
                timezone: "UTC",
                dateFormat: "MM/DD/YYYY",
                theme: "light",
                autoSave: true,
                compactMode: false,
              }}
            >
              <Form.Item name="language" label="Language">
                <Select size="large">
                  <Option value="en">English</Option>
                  <Option value="es">Spanish</Option>
                  <Option value="fr">French</Option>
                  <Option value="de">German</Option>
                </Select>
              </Form.Item>

              <Form.Item name="timezone" label="Timezone">
                <Select size="large">
                  <Option value="UTC">UTC</Option>
                  <Option value="EST">Eastern Time</Option>
                  <Option value="PST">Pacific Time</Option>
                  <Option value="GMT">Greenwich Mean Time</Option>
                </Select>
              </Form.Item>

              <Form.Item name="dateFormat" label="Date Format">
                <Select size="large">
                  <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                  <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                  <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                </Select>
              </Form.Item>

              <Form.Item name="theme" label="Theme">
                <Select size="large">
                  <Option value="light">Light</Option>
                  <Option value="dark">Dark</Option>
                  <Option value="auto">Auto</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="autoSave"
                label="Auto-save Changes"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="compactMode"
                label="Compact Mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                >
                  Save Preferences
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <>
                <BellOutlined /> Notifications
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              layout="vertical"
              initialValues={{
                emailNotifications: true,
                systemAlerts: true,
                securityAlerts: true,
                userRegistrations: true,
                systemUpdates: false,
                weeklyReports: true,
              }}
            >
              <Form.Item
                name="emailNotifications"
                label="Email Notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="systemAlerts"
                label="System Alerts"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="securityAlerts"
                label="Security Alerts"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="userRegistrations"
                label="New User Registrations"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="systemUpdates"
                label="System Updates"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="weeklyReports"
                label="Weekly Reports"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button type="primary" size="large">
                  Save Notification Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <>
                <SecurityScanOutlined /> Security
              </>
            }
          >
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Two-Factor Authentication:</Text>
                <br />
                <Text style={{ color: "#faad14" }}>Not Enabled</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  Enable 2FA
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Login Sessions:</Text>
                <br />
                <Text>3 active sessions</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  Manage Sessions
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>API Keys:</Text>
                <br />
                <Text>2 active keys</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  Manage Keys
                </Button>
              </div>

              <Divider />

              <Button type="primary" size="large" style={{ marginRight: 8 }}>
                Change Password
              </Button>
              <Button size="large">Download Backup Codes</Button>
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <>
                <GlobalOutlined /> Data & Privacy
              </>
            }
          >
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Data Export:</Text>
                <br />
                <Text>Export your account data</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  Request Export
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Activity Log:</Text>
                <br />
                <Text>View your account activity</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  View Log
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Privacy Settings:</Text>
                <br />
                <Text>Manage your privacy preferences</Text>
                <Button type="link" style={{ padding: 0, marginLeft: 8 }}>
                  Configure
                </Button>
              </div>

              <Divider />

              <div style={{ marginBottom: 16 }}>
                <Text strong>Account Deletion:</Text>
                <br />
                <Text type="secondary">
                  Permanently delete your account and all data
                </Text>
              </div>

              <Button danger size="large">
                Delete Account
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminAccountSettings;
